import { Component, inject, OnInit } from '@angular/core';
import { IIntegration } from "@shared/components/interfaces/integration.interface";
import { ActivatedRoute } from "@angular/router";
import { FeatureFlagEnum } from '@shared/enums/feature-flag.enum';
import { WaffleFeatureFlagsService } from '@shared/services/feature-flags/waffle-feature-flags.service';
import { IntegrationComponentState } from './integrations.component.state';
import { IIntegrationTemplate } from '@shared/interfaces/integration-template.interface';
import { IIntegrationLink } from '@shared/interfaces/integration-link.interface';
import { ApiInfoDto } from '@core/services/manage-organizations/manage-organization-dto';
import { AppConstants } from '@core/constants';
import { SessionService } from '@core/services/session.service';
import { RegionSettingsDto } from '@core/services/region-settings/region-settings-dto';
import { from, Observable } from 'rxjs';
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";

const IntegrationSlugFeatureFlagMapping: Record<string, string> = {
  cardioworld: FeatureFlagEnum.CARDIOWORLD_INTEGRATION,
  vqv: FeatureFlagEnum.VQV_INTEGRATION
};

// TODO: Replace with an actual country check eventually
const USA_COUNTRY_ID = 188;

@Component({
  selector: 'app-integrations',
  templateUrl: './integrations.component.html',
  styleUrls: ['./integrations.component.scss'],
  providers: [IntegrationComponentState],
})
export class IntegrationsComponent implements OnInit {
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private featureFlagService: WaffleFeatureFlagsService = inject(WaffleFeatureFlagsService);

  public integrationObject: IIntegration;
  public azumApiKey: string = '';

  public integrationTemplates: IIntegrationTemplate[] = [];
  public integrations: IIntegrationLink[] = [];
  public apiInfo: ApiInfoDto[] = [];
  public regions: RegionSettingsDto[] = [];

  constructor(
    private readonly state: IntegrationComponentState,
    private sessionService: SessionService,
    private snackBar: MatSnackBar,
  ) { }

  public ngOnInit(): void {
    if (this.activatedRoute.snapshot.data.data.api_keys.length) {
      this.azumApiKey = this.activatedRoute.snapshot.data.data.api_keys[0].key || '';
      this.integrationObject = {
        key: this.azumApiKey,
        name: 'AZUM'
      };
    }

    this.state.loadState();
    this.state.integrationTemplates$.subscribe(templates => this.integrationTemplates = templates);
    this.state.integrations$.subscribe(links => this.integrations = links);
    this.state.apiInfo$.subscribe(apiInfo => {
      this.apiInfo = apiInfo.filter(api => api?.alias != null && AppConstants.VISIBLE_API_INFO_ALIASES.includes(api.alias));
    });
    this.state.regions$.subscribe(regions => this.regions = regions);
  }


  public showIntegration(slug: string): boolean {
    if (slug === "nova") {
      return this.activatedRoute.snapshot.data.data.country?.id === USA_COUNTRY_ID;
    }

    // Special check for cardioworld
    if (slug === "cardioworld") {
      if (!this.regions) {
        return false;
      }

      // Exclude Switzerland and Norway
      const organizationCountryCode: string = this.activatedRoute.snapshot.data.data.country?.country_code;
      if (organizationCountryCode != null && ["ch", "no"].includes(organizationCountryCode.toLowerCase())) {
        return false;
      }

      const europeanRegions = this.regions.filter(region => region.name.startsWith("Europe"))
      const organizationCountryId = this.activatedRoute.snapshot.data.data.country?.id;

      let countryInEurope = false;
      for (const region of europeanRegions) {
        const countryInRegion = region.country.some(country => country.id === organizationCountryId);
        if (countryInRegion) {
          countryInEurope = true;
          break
        }
      }

      return countryInEurope;
    }

    if (slug === "vqv") {
      return true
    }

    const flagValue = this.featureFlagService.getValue(IntegrationSlugFeatureFlagMapping[slug]) as boolean ?? false;
    if (!flagValue) {
      return flagValue
    }

    return flagValue;
  }

  public showAPIKeys(): boolean {
    const authString: string = this.sessionService.getCookie('auth');
    const auth = JSON.parse(authString);
    return auth.is_admin as boolean;
  }

  public getIntegrationTemplate(slug: string) {
    return this.integrationTemplates.find(t => t.slug === slug);
  }

  public getIntegrationForTemplate(slug: string) {
    const integrationTemplate = this.getIntegrationTemplate(slug);
    if (!integrationTemplate) {
      return undefined
    }
    return this.integrations.find(integration => integration.template === integrationTemplate.id);
  }

  public copyApiKey(): any {
    const clipboardObs$: Observable<any> = from(navigator.clipboard.writeText(this.integrationObject.key));

    clipboardObs$
      .subscribe(
        {
          next: () => {
            this.snackBar.open('API Key copied to clipboard', 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
          },
          error: (error: any) => {
            this.snackBar.open('API Key did not copied', 'OK', AppConstants.TOAST_CONFIG.ERROR);
          },
        }
      );
  }
}
